import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectCurrentGame } from '@/store/gameSlice';
import { networkLogger, type NetworkLogEntry } from '@/services/networkLogger';
import { useDebugEnabled } from '@/debug/debugPreference';
import './DebugPanel.css';

export default function DebugPanel() {
  const game = useSelector(selectCurrentGame);
  const debugOn = useDebugEnabled();
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState<'avatars' | 'players' | 'map' | 'state' | 'network'>('avatars');
  const [networkLogs, setNetworkLogs] = useState<NetworkLogEntry[]>([]);

  useEffect(() => {
    const unsubscribe = networkLogger.onLogsUpdate((logs) => {
      setNetworkLogs(logs);
    });
    return unsubscribe;
  }, []);

  if (!debugOn || !game) {
    return null;
  }

  const renderAvatarsTab = () => {
    if (!game.avatars || game.avatars.length === 0) {
      return <div className="debug-empty">No avatars generated yet</div>;
    }

    return (
      <div className="debug-avatars-grid">
        {game.avatars.map((avatar) => (
          <div key={avatar.avatarId} className="debug-avatar-card">
            <div className="avatar-header">
              <div 
                className="avatar-color-badge" 
                style={{ backgroundColor: getColorHex(avatar.appearance.color) }}
              />
              <div className="avatar-info">
                <div className="avatar-id">{avatar.avatarId}</div>
                <div className="avatar-name">{avatar.displayName}</div>
              </div>
            </div>
            
            <div className="avatar-appearance">
              <div className="appearance-label">Appearance</div>
              <div className="appearance-value">
                {avatar.appearance.color} / {avatar.appearance.icon}
              </div>
            </div>

            <div className="avatar-skills">
              <div className="skill-row">
                <span className="skill-label">Mining:</span>
                <span className="skill-value">{avatar.skills.mining}</span>
                <div className="skill-bar">
                  <div 
                    className="skill-fill mining" 
                    style={{ width: `${(avatar.skills.mining / 70) * 100}%` }}
                  />
                </div>
              </div>
              
              <div className="skill-row">
                <span className="skill-label">Farming:</span>
                <span className="skill-value">{avatar.skills.farming}</span>
                <div className="skill-bar">
                  <div 
                    className="skill-fill farming" 
                    style={{ width: `${(avatar.skills.farming / 70) * 100}%` }}
                  />
                </div>
              </div>
              
              <div className="skill-row">
                <span className="skill-label">Solar:</span>
                <span className="skill-value">{avatar.skills.solarFarming}</span>
                <div className="skill-bar">
                  <div 
                    className="skill-fill solar" 
                    style={{ width: `${(avatar.skills.solarFarming / 70) * 100}%` }}
                  />
                </div>
              </div>
              
              <div className="skill-row">
                <span className="skill-label">Chemist:</span>
                <span className="skill-value">{avatar.skills.chemist}</span>
                <div className="skill-bar">
                  <div 
                    className="skill-fill chemist" 
                    style={{ width: `${(avatar.skills.chemist / 70) * 100}%` }}
                  />
                </div>
              </div>
              
              <div className="skill-row luck-row">
                <span className="skill-label">Luck:</span>
                <span className="skill-value">{avatar.skills.luck}</span>
                <div className="skill-bar">
                  <div 
                    className="skill-fill luck" 
                    style={{ width: `${(avatar.skills.luck / 15) * 100}%` }}
                  />
                </div>
              </div>
            </div>

            <div className="avatar-selection">
              {avatar.selectedBy ? (
                <div className="selected-by">
                  Selected by: {avatar.selectedBy}
                </div>
              ) : (
                <div className="not-selected">Not selected</div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderPlayersTab = () => {
    return (
      <div className="debug-players-list">
        {game.players.map((player) => (
          <div key={player.playerId} className="debug-player-card">
            <div className="player-header">
              <div className="player-number">#{player.playerNumber}</div>
              <div className="player-id">{player.playerId}</div>
              {player.isAI && <div className="ai-badge">AI</div>}
            </div>
            
            <div className="player-details">
              <div className="detail-row">
                <span className="detail-label">Avatar:</span>
                <span className="detail-value">
                  {player.avatarId || 'Not selected'}
                </span>
              </div>
              
              {player.isAI && (
                <>
                  <div className="detail-row">
                    <span className="detail-label">Provider:</span>
                    <span className="detail-value">{player.aiProvider}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Personality:</span>
                    <span className="detail-value">{player.aiPersonality}</span>
                  </div>
                </>
              )}
              
              <div className="detail-row">
                <span className="detail-label">Money:</span>
                <span className="detail-value">${player.money}</span>
              </div>
              
              <div className="detail-row">
                <span className="detail-label">Resources:</span>
                <span className="detail-value">
                  Energy: {player.resources?.energy || 0}, 
                  Minerals: {player.resources?.minerals || 0}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderMapTab = () => {
    return (
      <div className="debug-map-info">
        <div className="info-section">
          <h4>Map Details</h4>
            <div className="info-row">
              <span className="info-label">Level:</span>
              <span className="info-value">{game.level}</span>
            </div>
          <div className="info-row">
            <span className="info-label">Plots:</span>
            <span className="info-value">{game.map?.plots?.length || 0}</span>
          </div>
          <div className="info-row">
            <span className="info-label">Hex Radius:</span>
            <span className="info-value">{game.map?.hexRadius || 0}</span>
          </div>
          <div className="info-row">
            <span className="info-label">Base Location:</span>
            <span className="info-value">
              q:{game.map?.baseLocation?.q}, r:{game.map?.baseLocation?.r}
            </span>
          </div>
        </div>

        {game.landmark && (
          <div className="info-section">
            <h4>🌙 Lunar Landmark</h4>
            <div className="landmark-details">
              <div className="info-row">
                <span className="info-label">Name:</span>
                <span className="info-value">{game.landmark.displayName}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Type:</span>
                <span className="info-value">{game.landmark.type}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Coordinates:</span>
                <span className="info-value">
                  {game.landmark.latitude}°, {game.landmark.longitude}°
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Difficulty:</span>
                <span className="info-value">{game.landmark.difficulty}</span>
              </div>
              {game.landmark.description && (
                <div className="landmark-description">
                  {game.landmark.description}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderStateTab = () => {
    return (
      <div className="debug-state-json">
        <div className="json-header">
          <span>Game State JSON</span>
          <button 
            className="copy-button"
            onClick={() => {
              navigator.clipboard.writeText(JSON.stringify(game, null, 2));
            }}
          >
            Copy
          </button>
        </div>
        <pre className="json-content">
          {JSON.stringify(game, null, 2)}
        </pre>
      </div>
    );
  };

  const renderNetworkTab = () => {
    const formatTimestamp = (timestamp: number) => {
      const date = new Date(timestamp);
      return date.toLocaleTimeString() + '.' + date.getMilliseconds().toString().padStart(3, '0');
    };

    const getStatusColor = (status?: number) => {
      if (!status) return '#888';
      if (status >= 200 && status < 300) return '#4caf50';
      if (status >= 300 && status < 400) return '#ff9800';
      if (status >= 400) return '#f44336';
      return '#888';
    };

    const getTypeIcon = (type: NetworkLogEntry['type']) => {
      switch (type) {
        case 'request': return '📤';
        case 'response': return '📥';
        case 'sse': return '📡';
        case 'error': return '❌';
        default: return '📋';
      }
    };

    const copyNetworkLogs = () => {
      const logsText = JSON.stringify(networkLogs, null, 2);
      navigator.clipboard.writeText(logsText);
    };

    return (
      <div className="debug-network">
        <div className="network-header">
          <span>Network Activity ({networkLogs.length})</span>
          <div className="network-header-buttons">
            <button 
              className="copy-button"
              onClick={copyNetworkLogs}
            >
              Copy
            </button>
            <button 
              className="clear-button"
              onClick={() => networkLogger.clearLogs()}
            >
              Clear
            </button>
          </div>
        </div>
        <div className="network-list">
          {networkLogs.length === 0 ? (
            <div className="debug-empty">No network activity yet</div>
          ) : (
            networkLogs.slice().reverse().map((log) => (
              <div key={log.id} className={`network-entry network-entry-${log.type}`}>
                <div className="network-entry-header">
                  <span className="network-type-icon">{getTypeIcon(log.type)}</span>
                  <span className="network-timestamp">{formatTimestamp(log.timestamp)}</span>
                  {log.method && (
                    <span className="network-method" style={{ color: getStatusColor(log.status) }}>
                      {log.method}
                    </span>
                  )}
                  {log.url && (
                    <span className="network-url" title={log.url}>
                      {log.url.length > 60 ? log.url.substring(0, 60) + '...' : log.url}
                    </span>
                  )}
                  {log.status && (
                    <span className="network-status" style={{ color: getStatusColor(log.status) }}>
                      {log.status} {log.statusText}
                    </span>
                  )}
                  {log.duration !== undefined && (
                    <span className="network-duration">{log.duration}ms</span>
                  )}
                  {log.sseEvent && (
                    <span className="network-sse-type">{log.sseEvent.eventType}</span>
                  )}
                </div>
                {(log.requestBody || log.responseBody || log.sseEvent || log.error) && (
                  <details className="network-details">
                    <summary>Details</summary>
                    <div className="network-body">
                      {log.error && (
                        <div className="network-error">
                          <strong>Error:</strong> {log.error}
                        </div>
                      )}
                      {log.requestBody && (
                        <div className="network-request">
                          <strong>Request:</strong>
                          <pre>{JSON.stringify(log.requestBody, null, 2)}</pre>
                        </div>
                      )}
                      {log.responseBody && (
                        <div className="network-response">
                          <strong>Response:</strong>
                          <pre>{JSON.stringify(log.responseBody, null, 2)}</pre>
                        </div>
                      )}
                      {log.sseEvent && (
                        <div className="network-sse">
                          <strong>SSE Event ({log.sseEvent.eventType}):</strong>
                          <pre>{JSON.stringify(log.sseEvent.data, null, 2)}</pre>
                        </div>
                      )}
                    </div>
                  </details>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={`debug-panel ${isExpanded ? 'expanded' : 'collapsed'}`}>
      <div className="debug-header" onClick={() => setIsExpanded(!isExpanded)}>
        <span className="debug-title">🐛 Debug Panel</span>
        <span className="debug-toggle">{isExpanded ? '▼' : '▲'}</span>
      </div>

      {isExpanded && (
        <div className="debug-content">
          <div className="debug-tabs">
            <button
              className={`debug-tab ${activeTab === 'avatars' ? 'active' : ''}`}
              onClick={() => setActiveTab('avatars')}
            >
              Avatars ({game.avatars?.length || 0})
            </button>
            <button
              className={`debug-tab ${activeTab === 'players' ? 'active' : ''}`}
              onClick={() => setActiveTab('players')}
            >
              Players ({game.players?.length || 0})
            </button>
            <button
              className={`debug-tab ${activeTab === 'map' ? 'active' : ''}`}
              onClick={() => setActiveTab('map')}
            >
              Map & Landmark
            </button>
            <button
              className={`debug-tab ${activeTab === 'state' ? 'active' : ''}`}
              onClick={() => setActiveTab('state')}
            >
              Full State
            </button>
            <button
              className={`debug-tab ${activeTab === 'network' ? 'active' : ''}`}
              onClick={() => setActiveTab('network')}
            >
              Network ({networkLogs.length})
            </button>
          </div>

          <div className="debug-tab-content">
            {activeTab === 'avatars' && renderAvatarsTab()}
            {activeTab === 'players' && renderPlayersTab()}
            {activeTab === 'map' && renderMapTab()}
            {activeTab === 'state' && renderStateTab()}
            {activeTab === 'network' && renderNetworkTab()}
          </div>
        </div>
      )}
    </div>
  );
}

// Helper function to convert color names to hex
function getColorHex(color: string): string {
  const colors: Record<string, string> = {
    cyan: '#00ffff',
    magenta: '#ff00ff',
    yellow: '#ffff00',
    green: '#00ff00',
    orange: '#ff8800',
    purple: '#8800ff'
  };
  return colors[color] || '#ffffff';
}

